import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, MessageCircle, Plus, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const ReadingPage = () => {
  const { user, api } = useAuth();
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef(null);
  const [newDiscussion, setNewDiscussion] = useState({
    book_title: '',
    book_author: '',
    discussion_prompt: '',
    cover_image: '',
    cover_image_position: 50
  });
  const [isDraggingCover, setIsDraggingCover] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const coverDragRef = useRef(null);

  const handleCoverDragStart = (e) => {
    e.preventDefault();
    setIsDraggingCover(true);
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setDragStart({ y: clientY, startPos: newDiscussion.cover_image_position || 50 });
  };

  const handleCoverDragMove = useCallback((e) => {
    if (!isDraggingCover || !dragStart || !coverDragRef.current) return;
    e.preventDefault();
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    const containerHeight = coverDragRef.current.offsetHeight;
    const deltaY = clientY - dragStart.y;
    const deltaPercent = (deltaY / containerHeight) * 100;
    const newPos = Math.max(0, Math.min(100, dragStart.startPos - deltaPercent));
    setNewDiscussion(prev => ({ ...prev, cover_image_position: Math.round(newPos) }));
  }, [isDraggingCover, dragStart]);

  const handleCoverDragEnd = useCallback(() => {
    setIsDraggingCover(false);
    setDragStart(null);
  }, []);

  useEffect(() => {
    if (isDraggingCover) {
      window.addEventListener('mousemove', handleCoverDragMove);
      window.addEventListener('mouseup', handleCoverDragEnd);
      window.addEventListener('touchmove', handleCoverDragMove, { passive: false });
      window.addEventListener('touchend', handleCoverDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleCoverDragMove);
        window.removeEventListener('mouseup', handleCoverDragEnd);
        window.removeEventListener('touchmove', handleCoverDragMove);
        window.removeEventListener('touchend', handleCoverDragEnd);
      };
    }
  }, [isDraggingCover, handleCoverDragMove, handleCoverDragEnd]);

  useEffect(() => {
    const fetchDiscussions = async () => {
      try {
        const res = await api.get('/book-discussions');
        setDiscussions(res.data);
      } catch (error) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };

    fetchDiscussions();
  }, [api]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB');
      return;
    }
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/story-image', formData);
      setNewDiscussion(prev => ({ ...prev, cover_image: res.data.image_url }));
    } catch (err) {
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const handleCreateDiscussion = async (e) => {
    e.preventDefault();
    if (!newDiscussion.book_title.trim() || !newDiscussion.discussion_prompt.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/book-discussions', newDiscussion);
      setDiscussions([res.data, ...discussions]);
      setNewDiscussion({ book_title: '', book_author: '', discussion_prompt: '', cover_image: '' });
      setShowNewDialog(false);
    } catch (error) {
      // Non-critical error suppressed
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="reading-page">
      {/* Header */}
      <div className="relative mb-12">
        <div className="absolute inset-0 rounded-2xl overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1649220058039-e81e690e28ef"
            alt="Reading"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/90 to-brand-primary/70" />
        </div>
        <div className="relative p-8 md:p-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="text-white">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-6 h-6 text-brand-tertiary" />
                <span className="overline text-brand-tertiary">Book Discussions</span>
              </div>
              <h1 className="font-serif text-4xl font-semibold mb-4">What Are You Reading?</h1>
              <p className="text-white/80 max-w-lg">
                Discuss the books that inspire you. Share recommendations and discover 
                new reads from fellow writers.
              </p>
            </div>
            {user && (
              <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-brand-primary hover:bg-white/90 gap-2" data-testid="new-discussion-button">
                    <Plus className="w-4 h-4" />
                    Start Discussion
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Start a Book Discussion</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateDiscussion} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="book_title">Book Title *</Label>
                      <Input
                        id="book_title"
                        placeholder="Enter the book title"
                        value={newDiscussion.book_title}
                        onChange={(e) => setNewDiscussion({ ...newDiscussion, book_title: e.target.value })}
                        required
                        data-testid="book-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="book_author">Author</Label>
                      <Input
                        id="book_author"
                        placeholder="Who wrote this book?"
                        value={newDiscussion.book_author}
                        onChange={(e) => setNewDiscussion({ ...newDiscussion, book_author: e.target.value })}
                        data-testid="book-author-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="discussion_prompt">Discussion Prompt *</Label>
                      <Textarea
                        id="discussion_prompt"
                        placeholder="What would you like to discuss about this book?"
                        value={newDiscussion.discussion_prompt}
                        onChange={(e) => setNewDiscussion({ ...newDiscussion, discussion_prompt: e.target.value })}
                        required
                        rows={3}
                        data-testid="discussion-prompt-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cover Image (optional)</Label>
                      <input
                        type="file"
                        ref={coverInputRef}
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleCoverUpload}
                        className="hidden"
                      />
                      {newDiscussion.cover_image ? (
                        <div 
                          ref={coverDragRef}
                          className={`relative ${isDraggingCover ? 'cursor-grabbing' : 'cursor-grab'}`}
                          onMouseDown={handleCoverDragStart}
                          onTouchStart={handleCoverDragStart}
                        >
                          <img
                            src={newDiscussion.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${newDiscussion.cover_image}` : newDiscussion.cover_image}
                            alt="Cover preview"
                            className="w-full h-32 object-cover rounded-lg select-none pointer-events-none"
                            style={{ objectPosition: `center ${newDiscussion.cover_image_position || 50}%` }}
                            draggable={false}
                          />
                          <div className={`absolute inset-0 flex items-center justify-between px-3 transition-opacity ${isDraggingCover ? 'opacity-100' : 'opacity-0 hover:opacity-100'}`}>
                            <span className="text-white text-xs bg-black/50 px-2 py-1 rounded-full">Drag to reposition</span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setNewDiscussion(prev => ({ ...prev, cover_image: '', cover_image_position: 50 })); }}
                              className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full gap-2"
                          onClick={() => coverInputRef.current?.click()}
                          disabled={uploadingCover}
                          data-testid="upload-cover-btn"
                        >
                          {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                          {uploadingCover ? 'Uploading...' : 'Upload Cover Photo'}
                        </Button>
                      )}
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="btn-accent" disabled={submitting} data-testid="submit-discussion">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Start Discussion
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Discussions Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
        </div>
      ) : discussions.length === 0 ? (
        <div className="text-center py-20">
          <BookOpen className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">No discussions yet</h2>
          <p className="text-text-secondary mb-4">Be the first to start a book discussion!</p>
          {user && (
            <Button className="btn-primary" onClick={() => setShowNewDialog(true)}>
              Start a Discussion
            </Button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {discussions.map((discussion) => (
            <Link 
              key={discussion.id} 
              to={`/reading/${discussion.id}`}
              className="group"
              data-testid={`discussion-card-${discussion.id}`}
            >
              <Card className="h-full bg-white border-brand-primary/10 overflow-hidden card-hover">
                {discussion.cover_image && (
                  <div className="h-40 overflow-hidden">
                    <img 
                      src={discussion.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${discussion.cover_image}` : discussion.cover_image}
                      alt={discussion.book_title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      style={{ objectPosition: `center ${discussion.cover_image_position || 50}%` }}
                    />
                  </div>
                )}
                <CardContent className={`p-6 ${!discussion.cover_image ? 'pt-8' : ''}`}>
                  <h3 className="font-serif text-xl font-semibold text-text-primary mb-1 group-hover:text-brand-accent transition-colors">
                    {discussion.book_title}
                  </h3>
                  {discussion.book_author && (
                    <p className="text-sm text-text-muted mb-3">by {discussion.book_author}</p>
                  )}
                  <p className="text-text-secondary text-sm line-clamp-3 mb-4">
                    {discussion.discussion_prompt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-brand-primary/10 text-sm text-text-muted">
                    <span>Started by {discussion.created_by_name}</span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" />
                      {discussion.comment_count}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReadingPage;
